import React, { useRef, useEffect, useState } from 'react';
import { Box, Title, Text, Progress, Group, ThemeIcon, Paper, Grid } from '@mantine/core';
import { IconCode, IconDatabase, IconShield, IconUsers } from '@tabler/icons-react';

interface Skill {
  name: string;
  level: number;
  category: 'Frontend' | 'Backend' | 'Tools' | 'Soft Skills';
  color: string;
  description: string;
}

const skillsData: Skill[] = [
  // Frontend
  { name: 'React/Next.js', level: 95, category: 'Frontend', color: '#61DAFB', description: 'Advanced component architecture & SSR' },
  { name: 'TypeScript', level: 90, category: 'Frontend', color: '#3178C6', description: 'Type-safe application development' },
  { name: 'HTML/CSS', level: 95, category: 'Frontend', color: '#E34F26', description: 'Modern responsive design & animations' },
  { name: 'Three.js', level: 80, category: 'Frontend', color: '#000000', description: '3D graphics and interactive experiences' },
  
  // Backend
  { name: 'Node.js', level: 88, category: 'Backend', color: '#339933', description: 'Server-side JavaScript & APIs' },
  { name: 'Express.js', level: 85, category: 'Backend', color: '#000000', description: 'RESTful API development' },
  { name: 'MySQL', level: 82, category: 'Backend', color: '#4479A1', description: 'Database design & optimization' },
  { name: 'Firebase', level: 90, category: 'Backend', color: '#FFCA28', description: 'Real-time databases & hosting' },
  
  // Tools
  { name: 'Git/GitHub', level: 92, category: 'Tools', color: '#F05032', description: 'Version control & CI/CD workflows' },
  { name: 'OAuth 2.0', level: 85, category: 'Tools', color: '#EB5424', description: 'Authentication & authorization' },
  { name: 'Docker', level: 75, category: 'Tools', color: '#2496ED', description: 'Containerization & deployment' },
  { name: 'Discord.py', level: 96, category: 'Tools', color: '#5865F2', description: 'Bot development & API integration' },
  
  // Soft Skills
  { name: 'Problem Solving', level: 95, category: 'Soft Skills', color: '#9C27B0', description: 'Complex system architecture' },
  { name: 'Project Management', level: 88, category: 'Soft Skills', color: '#FF9800', description: 'Team coordination & planning' },
  { name: 'Communication', level: 90, category: 'Soft Skills', color: '#4CAF50', description: 'Technical documentation & mentoring' },
  { name: 'Learning Agility', level: 98, category: 'Soft Skills', color: '#E91E63', description: 'Rapid technology adoption' }
];

const categoryIcons = {
  'Frontend': IconCode,
  'Backend': IconDatabase,
  'Tools': IconShield,
  'Soft Skills': IconUsers
};

interface InteractiveSkillsProps {
  className?: string;
}

export const InteractiveSkills: React.FC<InteractiveSkillsProps> = ({ className = '' }) => {
  const [visibleSkills, setVisibleSkills] = useState<Set<string>>(new Set());
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const skillName = entry.target.getAttribute('data-skill');
            if (skillName) {
              setVisibleSkills(prev => new Set([...Array.from(prev), skillName]));
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    const skillElements = containerRef.current?.querySelectorAll('[data-skill]');
    skillElements?.forEach(el => observer.observe(el));

    return () => {
      skillElements?.forEach(el => observer.unobserve(el));
    };
  }, []);

  const groupedSkills = skillsData.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {} as Record<string, Skill[]>);

  return (
    <Box ref={containerRef} className={className}>
      <Title order={2} size="2.5rem" fw={700} ta="center" mb="xl" c="white">
        Technical Expertise
      </Title>
      
      <Grid gutter="xl">
        {Object.entries(groupedSkills).map(([category, skills]) => {
          const IconComponent = categoryIcons[category as keyof typeof categoryIcons];
          
          return (
            <Grid.Col key={category} span={{ md: 6 }}>
              <Paper
                p="xl"
                radius="lg"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transform: 'translateY(-5px)'
                  }
                }}
              >
                <Group mb="lg" align="center">
                  <ThemeIcon
                    size={50}
                    radius="md"
                    variant="gradient"
                    gradient={{ from: 'blue.6', to: 'cyan.4' }}
                  >
                    <IconComponent size={28} />
                  </ThemeIcon>
                  <Title order={3} c="white" fw={600}>
                    {category}
                  </Title>
                </Group>

                <Box>
                  {skills.map((skill, index) => (
                    <Box
                      key={skill.name}
                      data-skill={skill.name}
                      mb="lg"
                      onMouseEnter={() => setHoveredSkill(skill.name)}
                      onMouseLeave={() => setHoveredSkill(null)}
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        transform: hoveredSkill === skill.name ? 'scale(1.02)' : 'scale(1)',
                        opacity: hoveredSkill && hoveredSkill !== skill.name ? 0.7 : 1
                      }}
                    >
                      <Group justify="space-between" mb="xs">
                        <Text fw={500} c="white" size="sm">
                          {skill.name}
                        </Text>
                        <Text
                          size="sm"
                          fw={600}
                          style={{
                            color: '#61DAFB',
                            opacity: visibleSkills.has(skill.name) ? 1 : 0,
                            transition: 'opacity 0.5s ease',
                            transitionDelay: `${index * 0.1}s`
                          }}
                        >
                          {skill.level}%
                        </Text>
                      </Group>
                      
                      <Progress
                        value={visibleSkills.has(skill.name) ? skill.level : 0}
                        size="md"
                        radius="xl"
                        color={skill.color}
                        style={{
                          transition: `all 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s`,
                          '& .mantine-Progress-bar': {
                            background: skill.color,
                            boxShadow: hoveredSkill === skill.name 
                              ? `0 0 20px ${skill.color}66` 
                              : `0 0 10px ${skill.color}33`,
                            transition: 'box-shadow 0.3s ease'
                          }
                        }}
                      />
                      
                      {hoveredSkill === skill.name && (
                        <Text
                          size="xs"
                          c="dimmed"
                          mt="xs"
                          style={{
                            animation: 'fadeInUp 0.3s ease-out',
                            borderLeft: `3px solid ${skill.color}`,
                            paddingLeft: '0.5rem'
                          }}
                        >
                          {skill.description}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid.Col>
          );
        })}
      </Grid>

      {/* Overall skill summary */}
      <Paper
        mt="xl"
        p="xl"
        radius="lg"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          border: '1px solid rgba(102, 126, 234, 0.2)',
          textAlign: 'center'
        }}
      >
        <Title order={3} c="white" mb="md">
          Skill Summary
        </Title>
        <Group justify="center" gap="xl">
          {Object.entries(groupedSkills).map(([category, skills]) => {
            const avgLevel = Math.round(skills.reduce((sum, skill) => sum + skill.level, 0) / skills.length);
            return (
              <Box key={category} style={{ textAlign: 'center' }}>
                <Text size="xl" fw={700} c="white">
                  {avgLevel}%
                </Text>
                <Text size="sm" c="dimmed">
                  {category}
                </Text>
              </Box>
            );
          })}
        </Group>
      </Paper>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </Box>
  );
};

export default InteractiveSkills; 